/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2016, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 * @module dashboard/filter/pagecontext/PageContextTupleSet
 * @see PageContextTupleSet
 */
define([
	'../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'../../../app/nls/StringResources',
	'underscore'
], function(Class, resources, _) {

	/**
	 * Model which represents a single entry in the pageContext collection (and can be persisted to the dashboard spec).
	 * This entry is externally exposed as
	 * 1) a pageContextItem (an object API) or
	 * 2) a pageContextSpecItem (a JSON spec for use in the idvisualizations's setContext api).
	 */
	var PageContextTupleSet = Class.extend({
		init: function(attrs /*, options*/ ) {
			PageContextTupleSet.inherited('init', this, attrs);

			_.each(attrs, function(values, keys) {
				this[keys] = values;
			}.bind(this));

			this.api = {
				getValues: this.getValues.bind(this),
				getValueIds: this.getValueIds.bind(this),
				getValueCaptions: this.getValueCaptions.bind(this),
				getValueCaption: this.getValueCaption.bind(this),
				getValueCount: this.getValueCount.bind(this),
				getValueId: this.getValueId.bind(this),
				getValueParentIds: this.getValueParentIds.bind(this),
				getValueParentId: this.getValueParentId.bind(this),
				getExclude: this.getExclude.bind(this),
				getIsNamedSet: this.getIsNamedSet.bind(this),
				isDataPointType: this.isDataPointType.bind(this),
				getDataPointCount: this.getDataPointCount.bind(this),
				getDataPoints: this.getDataPoints.bind(this),
				getDataPoint: this.getDataPoint.bind(this),
				getDataPointValues: this._getDataPointValues.bind(this),
				getExcludeForSpec: this.getExcludeForSpec.bind(this),
				getNetValues: this.getNetValues.bind(this)
			};

			var existingValuesMap = (this.get()) ? _.clone(this.get()) : {};
			_.each(existingValuesMap, function(existingValue) {
				this._addLookupKey(existingValue.u);
			}.bind(this));
		},

		/**
		 * @returns the 'public api' for a PageContextTupleSet (a pageContextItem as returned by pageContext's getPageContextItems)
		 */
		getAPI: function() {
			return this.api;
		},

		/**
		 * If this is a tab filter, we need to be able to acces the related globalContext for this item if there is one.
		 */
		getGlobalContext: function() {
			return (this._globalContextCb) ? this._globalContextCb() : null;
		},

		/**
		 * Called by the parent pageContext on item initialization if this item is a tab filter and has a global counterpart.
		 * @param globalContextCb - a callback function which can be used to obtain the global counterpart of this tab filter.
		 */
		setGlobalContextCb: function(globalContextCb) {
			this._globalContextCb = globalContextCb;
		},

		/**
		 * getPageContextItems API Method:
		 * @returns the count of values for this pageContextItem which could be a 'net set of values' if this is a tab filter and a global filter on the same item exists.
		 */
		getValueCount: function() {
			return (this.getGlobalContext()) ? this._getValueCount(this._intersectTupleSets(this.getGlobalContext(), /*includeTabValuesWhenEmpty*/ true)) : this._getValueCount();
		},

		/**
		 * getPageContextItems API Method
		 * same as _getValueIds but takes the globalContext into account to produce a 'net set' of valueIds.
		 * @returns an array of id's (one per value - currently, AKA muns!) for this pageContextItem
		 */
		getValueIds: function() {
			return this.getGlobalContext() ? this._getValueIds(this._intersectTupleSets(this.getGlobalContext(), /*includeTabValuesWhenEmpty*/ true)) : this._getValueIds();
		},

		getValues: function() {
			return this.getGlobalContext() ? this._getValues(this._intersectTupleSets(this.getGlobalContext(), /*includeTabValuesWhenEmpty*/ true)) : this._getValues();
		},

		/**
		 * same as getValues but takes the globalContext into account to produce a 'net set' of values.
		 * @returns the value for this pageContextItem which could be a 'net set of values' if this is a tab filter and a global filter on the same item exists.
		 */
		getNetValues: function() {
			return _.flatten(this.getSpec());
		},

		/**
		 * getPageContextItems API Method:
		 * same as _getValueCaptions but takes the globalContext into account to produce a 'net set' of valueCaptions.
		 * @returns the value captions for this pageContextItem which could be a 'net set of values' if this is a tab filter and a global filter on the same item exists.
		 */
		getValueCaptions: function() {
			return this.getGlobalContext() ? this._getValueCaptions(this._intersectTupleSets(this.getGlobalContext(), /*includeTabValuesWhenEmpty*/ true)) : this._getValueCaptions();
		},

		/**
		 * getPageContextItems API Method:
		 * @param valueIdx - the value's index
		 * @returns the nth value's id (mun)
		 */
		getValueId: function(valueIdx) {
			valueIdx = (!valueIdx) ? 0 : valueIdx;

			var valueIds = this.getValueIds();
			return (valueIds && valueIdx < valueIds.length) ? valueIds[valueIdx] : null;
		},

		/**
		 * getPageContextItems API Method:
		 * @param valueIdx - the value's index
		 * @returns the nth value's caption.
		 */
		getValueCaption: function(valueIdx) {
			valueIdx = (!valueIdx) ? 0 : valueIdx;

			var valueCaptions = this.getValueCaptions();
			return (valueCaptions && valueIdx < valueCaptions.length) ? valueCaptions[valueIdx] : null;
		},

		/**
		 * getPageContextItems API Method:
		 * same as _getValueParentIds but takes the globalContext into account to produce a 'net set' of valueParents.
		 * @returns the value parents for this pageContextItem which could be a 'net set of values' if this is a tab filter and a global filter on the same item exists.
		 */
		getValueParentIds: function() {
			return this.getGlobalContext() ? this._getValueParentIds(this._intersectTupleSets(this.getGlobalContext(), /*includeTabValuesWhenEmpty*/ true)) : this._getValueParentIds();
		},

		/**
		 * getPageContextItems API Method:
		 * @param valueIdx - the value's index
		 * @returns the nth value's parent's id.
		 */
		getValueParentId: function(valueIdx) {
			valueIdx = (!valueIdx) ? 0 : valueIdx;

			var valueParents = this.getValueParentIds();
			return (valueParents && valueIdx < valueParents.length) ? valueParents[valueIdx] : null;
		},

		/**
		 * getPageContextItems API Method:
		 * DataPoints type tupleSet is in double nested array while others are map style.
		 * @returns true if tupleSet is a DataPoints type
		 */
		isDataPointType: function() {
			var tupleSet = this.get();
			if (tupleSet) {
				var keys = _.keys(tupleSet);
				var valueOrTuple = tupleSet[keys[0]];

				return (Array.isArray(valueOrTuple) && valueOrTuple.length >= 2);
			}
			return false;
		},

		/**
		 * getPageContextItems API Method:
		 * @returns array of DataPoints
		 */
		getDataPoints: function() {
			return this._getDataPoints();
		},

		/**
		 * getPageContextItems API Method:
		 * @param valueIdx - the value's index
		 * @returns the nth value's DataPoints.
		 */
		getDataPoint: function(valueIdx) {
			valueIdx = (!valueIdx) ? 0 : valueIdx;

			var values = this.getDataPoints();
			return (values && valueIdx < values.length) ? values[valueIdx] : null;
		},

		/**
		 * getPageContextItems API Method:
		 * @returns count of DataPoints
		 */
		getDataPointCount: function() {
			var values = this.getDataPoints();
			return (values && values.length > 0) ? values.length : 0;
		},

		/**
		 * @returns this tupleSet in the form required by the pageContextSpec (as needed in setPageContext for the cardWidget).
		 */
		getSpec: function() {
			var globalContext = this.getGlobalContext();
			return (globalContext) ? this._intersectTupleSetsForSpec(globalContext) : this.getValuesArray();
		},

		/**
		 * used by updatePageContext to update the values when (for example) a user has selected or deselected them
		 * @param update - an update specification which includes a command (update|remove) and an array of values
		 * @returns true if the values were modified.
		 */
		mergeValues: function(update, options) {
			//TODO: for now, assume valueOrTuple is keyed on a single mun (which is true for filters).
			var result;
			switch (update.command) {
				case 'remove':
					result = this._removeValues(update);
					break;
				case 'update':
					result = this._updateValues(update);
					break;
				case 'replace':
					result = this._replaceValues(update);
					break;
				default:
					result = {
						modified: false
					};
					break;
			}

			if (result.modified) {
				this.set(result.valuesMap, options);
			}
			return result.modified;
		},

		_removeValues: function(update) {
			var modified = false;
			var existingValuesMap = (this.get()) ? _.clone(this.get()) : {};
			_.each(update.values, function(valueOrTuple) {
				var mapKey = this._createLookupKey(valueOrTuple);
				if (this._existsInMap(mapKey)) {
					existingValuesMap[mapKey] = null;
					delete existingValuesMap[mapKey];
					this._removeLookupKey(mapKey);
					modified = true;
				}
			}.bind(this));
			return {
				modified: modified,
				valuesMap: existingValuesMap
			};
		},

		_updateValues: function(update) {
			var modified = false;
			var existingValuesMap = (this.get()) ? _.clone(this.get()) : {};

			_.each(update.values, function(valueOrTuple) {
				var mapKey = this._createLookupKey(valueOrTuple);
				if (!this._existsInMap(mapKey)) {
					existingValuesMap[mapKey] = this._createTupleValue(valueOrTuple);
					this._addLookupKey(mapKey);
					modified = true;
				}
			}.bind(this));
			return {
				modified: modified,
				valuesMap: existingValuesMap
			};
		},

		_replaceValues: function(update) {
			var modified = false;
			var valuesMap = {};

			// if update is empty, delete existingValuesMap
			if (!Object.keys(update.values).length) {
				var existingValuesMap = (this.get()) ? _.clone(this.get()) : {};
				_.each(existingValuesMap, function(valueOrTuple) {
					var mapKey = this._createLookupKey(valueOrTuple);
					if (this._existsInMap(mapKey)) {
						existingValuesMap[mapKey] = null;
						delete existingValuesMap[mapKey];
						this._removeLookupKey(mapKey);
					}
					modified = true;
				}.bind(this));
			}

			_.each(update.values, function(valueOrTuple) {
				var mapKey = this._createLookupKey(valueOrTuple);
				valuesMap[mapKey] = this._createTupleValue(valueOrTuple);
				this._addLookupKey(mapKey);
				modified = true;
			}.bind(this));

			return {
				modified: modified,
				valuesMap: valuesMap
			};
		},

		/**
		 * The valueOrTuple from Filter is always an object wit 'u' and 'd' properties.
		 * The 'u', useValue, is used as key in this case.
		 *
		 * The valueOrTuple from visualization can be an array for dataPoint case.
		 * The concatenated 'u', useValue, of all edges in the array is used as key.
		 *
		 * ex) Filter: key value is 'Web'
		 *				{
		 *					'u': 'Web',
		 *					'd': 'Web'
		 *				}
		 *
		 * ex) dataPoint: key value is 'Camping EquipmentWeb'
		 *				[{
		 *				'u': 'Camping Equipment',
		 *				'd': 'Camping Equipment'
		 *				},
		 *				{
		 *				'u': 'Web',
		 *				'd': 'Web'
		 *				}]
		 */
		_createLookupKey: function(valueOrTuple) {
			if (_.isArray(valueOrTuple)) {
				var key = '';
				for (var i = 0; i < valueOrTuple.length; i++) {
					var tuplePart = valueOrTuple[i];
					key = key.concat(tuplePart.u);
				}
				return key;
			}
			return valueOrTuple.u;
		},

		_createTupleValue: function(valueOrTuple) {
			if (_.isArray(valueOrTuple)) {
				return valueOrTuple;
			}
			var value = {
				u: valueOrTuple.u,
				d: valueOrTuple.d
			};
			if (valueOrTuple.p) {
				value.p = valueOrTuple.p;
			}
			return value;
		},

		_existsInMap: function(key) {
			return !!(this._keyMap && this._keyMap[key]);
		},

		_addLookupKey: function(key) {
			if (!this._keyMap) {
				this._keyMap = {};
			}

			this._keyMap[key] = true;
		},

		_removeLookupKey: function(key) {
			if (!this._keyMap) {
				return;
			}

			delete this._keyMap[key];
		},

		/**
		 * add a new tupleSet to the existing one
		 * @param newTupleSet - the tupleSet to append to the existing one (which could contain one or more tuples to be added)
		 *                   (a tuple can be an edge entry like '2005' or can represent a datapoint (eg a bubble in a bubblechart) like '2005/Camping/Fax')
		 *					 each tuplePart is made up of a mun and a key).
		 * @param options - options to pass along to set (such as undo/Redo information).
		 */
		add: function(newTupleSet, options) {
			var existingValues = (this.get()) ? _.clone(this.get()) : [];
			_.each(newTupleSet, function(newTuple) {
				existingValues.push(newTuple);
			});
			this.set(existingValues, options);
		},

		/**
		 * @param tupleSet - if passed in, a specific tupleSet can be processed (such as the one returned by intersectTupleSets).  Otherwise, this objects tupleSet is assumed.
		 * @returns the count of values for this pageContextItems
		 */
		_getValueCount: function(tupleSet) {
			tupleSet = tupleSet || this.get();
			return (_.isArray(tupleSet)) ? tupleSet.length : (tupleSet ? _.keys(tupleSet).length : 0);
		},

		/**
		 * Note: This api now supports a tupleSet in 'map form' as is used internally or in array form (as is returned in getValuesArray)
		 * @param tupleSet - if passed in, a specific tupleSet can be processed (such as the one returned by intersectTupleSets).  Otherwise, this objects tupleSet is assumed.
		 * @returns an array of captions (one per value) for this pageContextItem
		 */
		_getValueCaptions: function(tupleSet) {
			tupleSet = tupleSet || this.get();
			var tupleCaptionArray = [];
			if (tupleSet) {
				_.each(tupleSet, function(tupleSetEntry) {
					var caption = (_.isArray(tupleSetEntry) && tupleSetEntry[0].d) ? tupleSetEntry[0].d : tupleSetEntry.d;
					tupleCaptionArray.push(_.isEmpty('' + caption) ? resources.get('filterBlank') : caption);
				}.bind(this));
			}
			return tupleCaptionArray;
		},

		/**
		 * Note: This api now supports a tupleSet in 'map form' as is used internally or in array form (as is returned in getValuesArray)
		 * @param tupleSet - if passed in, a specific tupleSet can be processed (such as the one returned by intersectTupleSets).  Otherwise, this objects tupleSet is assumed.
		 * @returns an array of id's (one per value - currently, AKA muns!) for this pageContextItem
		 */
		_getValueIds: function(tupleSet) {
			tupleSet = tupleSet || this.get();
			var tupleIdArray = [];
			if (tupleSet) {
				_.each(tupleSet, function(tupleSetEntry) {
					var tupleKey = this._createLookupKey(tupleSetEntry);
					tupleIdArray.push(tupleKey);
				}.bind(this));
			}
			return tupleIdArray;
		},

		/**
		 * Similar to getValueIds and getValueCaptions except it returns
		 * 1) an aray of n {u,d} objects for n single element selections (ie: {u: 'a->[Camping]', d:'Camping Equipment'})
		 * 2) an array of n [{u,d},{u,d}] arrays for multi-element selections
		 * @param tupleSet - if passed in, a specific tupleSet can be processed (such as the one returned by intersectTupleSets).  Otherwise, this objects tupleSet is assumed.
		 * @returns an array as described above.
		 */
		_getValues: function(tupleSet) {
			tupleSet = tupleSet || this.get();
			var tupleArray = [];
			if (tupleSet) {
				_.each(tupleSet, function(tupleSetEntry) {
					//var tupleKey = this._createLookupKey(tupleSetEntry);
					var value = (Array.isArray(tupleSetEntry) && tupleSetEntry.length === 1) ? tupleSetEntry[0] : tupleSetEntry;
					tupleArray.push(value);
				}.bind(this));
			}
			return tupleArray;
		},

		/**
		 * @param itemId - the hierarchy id of the values
		 * @returns an [{u,d},{u,d}] array of the data point values of the hierarchy above.
		 */
		_getDataPointValues: function(itemId) {
			var datapoints = this.getDataPoints();
			var hierIdx = -1;
			this.hierarchies.forEach(function(hierarchy, index) {
				if (hierarchy.hierarchyUniqueName === itemId){
					hierIdx = index;
					return;
				}
			}.bind(this));
			if (hierIdx !== -1) {
				var values = [];
				for (var i = 0; i < datapoints.length; i++) {
					values.push(datapoints[i][hierIdx]);
				}
				return values;
			}
		},

		/**
		 * Note: This api now supports a tupleSet in 'map form' as is used internally or in array form (as is returned in getValuesArray)
		 * @param tupleSet - if passed in, a specific tupleSet can be processed (such as the one returned by intersectTupleSets).  Otherwise, this objects tupleSet is assumed.
		 * @returns an array of id's (one per value - currently, AKA muns!) for this pageContextItem
		 */
		_getValueParentIds: function(tupleSet) {
			tupleSet = tupleSet || this.get();
			var tupleIdArray = [];
			if (tupleSet) {
				_.each(tupleSet, function(tupleSetEntry) {
					if (tupleSetEntry[0].parentId) {
						tupleIdArray.push(tupleSetEntry[0].parentId);
					}
				});
			}
			return tupleIdArray;
		},

		/**
		 * perform an intersection of two tupleSets based on the contributors (global and tab level tupleSets.)
		 * @param globalPageContextEntry - If both a global and tab-level entry exist, this will be set. If exclude is set on either, the global will be ignored.
		 * @param includeTabValuesWhenEmpty - this flag controls the case where global and tab filters for the same item exist but the tab item list is empty.
		 *			If false, the global values are returned when no tab values remain in the intersection (this Net context is used for the card widget)
		 *			If true, an empty result is returned when no tab values remain in the intersection (this Net context is used for selected value counts/captions in the filter dock.)
		 */
		_intersectTupleSets: function(globalPageContextEntry, includeTabValuesWhenEmpty) {
			var globalTupleSet = globalPageContextEntry ? globalPageContextEntry.tupleSet : null;
			var glbExclude = globalPageContextEntry && globalPageContextEntry.getExclude() || false;

			//There is NO global context, return the tab tupleset.
			if (!globalTupleSet) {
				return this.getValuesArray();
			}
			//There is a global context item but it has no values.
			//This means that the tab filter would show no values either.  Just return unfiltered.
			if (!_.keys(globalTupleSet).length) {
				return this.getValuesArray();
			}

			var resultingTupleSet = {};
			var visibleCheckedCount = 0;
			//There is a global context with values. There are 2 cases (details below)

			// Global exclude
			if (glbExclude) {
				_.each(this.get(), function(tuple, key) {
					if (!globalTupleSet[key]) {
						resultingTupleSet[key] = tuple;
					}
				}.bind(this));
				return this.getValuesArray(resultingTupleSet);
			}

			// Special Case: global not exclude tab exclude and merge gives nothing
			// Can only happen using global as tab
			// Eg. Tab filter selects A and B and also invert,
			// THEN Global filter also selects A and B.

			if (this.getExclude()) {
				let diffValue = _.find(this.getValuesArray(globalTupleSet), tuple => {
					return this.get() && _.keys(this.get()).indexOf(tuple[0].u) === -1;
				});
				if (diffValue === undefined) {
					return [];
				}
			}

			// Global not exclude
			//For each checked value at the tab level, confirm that the value (or an ancestor) at the global level is checked.
			//If it is, the selection should be included....otherwise, its not.  Build a reverse-keyed map (keyed by path not the mun).
			var globalTupleSetByPath = {};
			_.each(globalTupleSet, function(tuple, key) {
				globalTupleSetByPath[JSON.stringify(tuple)] = key;
			}.bind(this));

			_.each(this.get(), function(tuple, key) {
				if (this._isMemberAtGlobalLevelSelected(globalTupleSetByPath, tuple)) {
					resultingTupleSet[key] = tuple;
					visibleCheckedCount++;
				}
			}.bind(this));

			if (visibleCheckedCount === 0) {
				//case 1) nothing is checked (or all checks at the tab level are not in the global value set) - use the global context.
				return (includeTabValuesWhenEmpty) ? [] : this.getValuesArray(globalTupleSet);
			}

			return this.getValuesArray(resultingTupleSet);
		},

		//Return true if the corresponding global member or a global ancestor member of tuple value is selected.
		_isMemberAtGlobalLevelSelected: function(globalTupleSetByValue, value) {
			if (globalTupleSetByValue[JSON.stringify(value)]) {
				return true;
			}
			return false;
		},

		/**
		 * Intersecting tuples for VisControl spec is used in conjunction with pageContextEntry.getExcludeForSpec.
		 *
		 * @param globalPageContextEntry - there are 6 cases. see comments below
		 */
		_intersectTupleSetsForSpec: function(globalPageContextEntry) {

			var globalTupleSet = globalPageContextEntry ? globalPageContextEntry.tupleSet : null;
			var glbExclude = (globalPageContextEntry && globalPageContextEntry.getExclude()) || false;
			var hasGlobalSelectedMembers = (globalPageContextEntry && globalPageContextEntry.getValueCount()) || 0;

			var tabExclude = this.getExclude();
			var hasLocalSelectedMembers = this.getValueCount();

			// Case 1: global has something selected, and none in tab, return global tuple set
			if (hasGlobalSelectedMembers && !hasLocalSelectedMembers) {
				return this.getValuesArray(globalTupleSet);
			}

			// Case 2: global and tab have something selected, check invert conditions
			if (hasGlobalSelectedMembers) {
				var glbValues = this.getValuesArray(globalTupleSet);

				if (!tabExclude) {
					// Case 2-1: tab is normal mode and global is inverted
					// Case 2-4: tab is normal mode and global is normal mode
					return this._intersectTupleSets(globalPageContextEntry);
				}

				if (!glbExclude) {
					// Case 2-2:  global is normal mode and tab is exclude. The exclude flag for spec is False (see pageContextEntry.getExcludeForSpec).
					// Exclude selected values of tab from global.
					var finalValues = [];
					glbValues.forEach(function(tuple) {
						var foundInTab = (this.get() && this.get()[tuple[0].u]);
						if (!foundInTab) {
							finalValues.push(tuple);
						}
					}.bind(this));

					// Special case: finalValues being [] is not allowed if caused by tab filter
					// invert will be disabled for this case. If caused by global
					// will use what is in global. See Defect 263292
					return finalValues;
				}

				if (glbExclude) {
					// Case 2-3:  global and tab are both inverted...exclude all values in both sets. The exclude flag for spec is True (see pageContextEntry.getExcludeForSpec).
					// Exclude all values in both sets.
					return glbValues.concat(this.getValuesArray());
				}
			}
			// Case 3: There is no global for the same hierarchy. Use tab values
			return this.getValuesArray();
		},

		/**
		 * provide 'set' model access to the exclude property.
		 * @param {boolean} state - the exclude state to save.
		 */
		setExclude: function(state) {
			this.exclude = state;
		},

		/**
		 * provide 'get' model access to the exclude property.
		 * @returns the saved exclude flag
		 */
		getExclude: function() {
			return !!this.exclude;
		},

		/**
		 * provide 'get' model access to the isNamedSet property.
		 * @returns the saved isNamedSet flag
		 */
		getIsNamedSet: function() {
			return !!this.isNamedSet;
		},

		/**
		 * The exclude flag of the spec to VisControl needs to cross check the exclude flag in global context as well as if it has selected members.
		 * This function is used in conjunction with PageContextTupleSet._intersectTuplesForSpec.
		 *
		 * @returns {boolean} there are 4 cases. see comments below
		 */
		getExcludeForSpec: function() {
			var globalPageContextEntry = this.getGlobalContext();
			var glbExclude = (globalPageContextEntry && globalPageContextEntry.getExclude()) || false;
			var hasGlobalSelectedMembers = (globalPageContextEntry && globalPageContextEntry.getValueCount()) || 0;

			var tabExclude = this.getExclude();
			var hasLocalSelectedMembers = this.getValueCount();

			// Case 1: global has no selected members but local tab has some selected. Return exclude value of local tab
			if (!hasGlobalSelectedMembers) {
				return tabExclude;
			}

			// Case 2: global has some selected members but local tab has none. Return exclude value of global
			if (!hasLocalSelectedMembers) {
				return glbExclude;
			}

			// Case 3: global has some selected members and local tab has some. Return true if both are exclude
			return glbExclude && tabExclude;
		},

		/**
		 * The tupleSet is stored as a map but the pageContextSpecItem needs an array of arrays.
		 * Each entry in the outer array represents a single selected value
		 * Each entry in the inner array represents the edge values that make up that value.
		 * For filters, we only ever have one edge.
		 * For brushing, there could be 1 or more depending on how many edge values define a selected datapoint.
		 * @param tupleSetMap - the tupleSetMap as in a PageContextTupleSet
		 * @returns an array of the map content.
		 */
		getValuesArray: function(tupleSet) {
			tupleSet = tupleSet || this.get();
			if (Array.isArray(tupleSet)) {
				//If the tupleSet is stored as an array (brushing), simply return it.
				return tupleSet;
			}
			var tupleSetArray = [];
			if (tupleSet) {
				//If the tupleSet is stored as a map (filters), convert to an array.
				_.each(tupleSet, function(tuple) {
					if (_.isArray(tuple)) {
						tupleSetArray.push(tuple);
					} else {
						tupleSetArray.push([tuple]);
					}

				}.bind(this));
			}
			return tupleSetArray;
		},

		hasData: function() {
			let tupleSet = this.get();
			return tupleSet || this.getExclude() || this.getIsNamedSet() ? true: false;
		},

		/**
		 * DataPoints type tupleSet is in double nested array while others are map style.
		 * @return values if tupleSet value is array, or return null
		 */
		_getDataPoints: function() {

			if (this.isDataPointType()) {
				return _.values(this.get());
			}
			return null;
		}

	});

	return PageContextTupleSet;
});
